import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { customerAPI } from '../../services/api';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

const EditCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await customerAPI.getCustomer(id);
        const payload = res?.data?.data;
        if (payload?.customer) {
          const c = payload.customer;
          reset({
            address_line_1: c.address_line_1 || '',
            address_line_2: c.address_line_2 || '',
            city: c.city || '',
            state: c.state || '',
            pincode: c.pincode || '',
          });
        } else {
          toast.error('Customer not found');
          navigate('/customers');
        }
      } catch (e) {
        toast.error('Failed to load customer');
        navigate('/customers');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate, reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      await customerAPI.updateCustomer(id, data);
      toast.success('Customer updated');
      queryClient.invalidateQueries(['customer', id]);
      queryClient.invalidateQueries(['customers']);
      navigate(`/customers/${id}`);
    } catch (e) {
      const resp = e?.response?.data;
      if (Array.isArray(resp?.errors)) {
        resp.errors.forEach((err) => {
          if (err?.field && err?.message) setError(err.field, { type: 'server', message: err.message });
        });
        toast.error('Please fix the highlighted errors');
      } else {
        toast.error(resp?.message || 'Failed to update customer');
      }
    } finally {
      setSaving(false);
    }
  };

  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('photo', file);
    try {
      await customerAPI.uploadPhoto(id, formData);
      toast.success('Photo uploaded');
      queryClient.invalidateQueries(['customer', id]);
    } catch (err) {
      toast.error('Failed to upload photo');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link to={`/customers/${id}`} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Profile
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <h1 className="text-xl font-semibold text-gray-900">Edit Customer</h1>

        <div>
          <label className="block text-sm font-medium text-gray-700">Customer Photo</label>
          <input type="file" accept="image/*" className="mt-1" onChange={onPhotoSelected} />
          <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB.</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
            <input className="input mt-1" {...register('address_line_1', { required: 'Address is required', minLength: { value: 10, message: 'Minimum 10 characters' } })} />
            {errors.address_line_1 && <p className="text-sm text-red-600 mt-1">{errors.address_line_1.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Address Line 2</label>
            <input className="input mt-1" {...register('address_line_2')} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">City</label>
              <input className="input mt-1" {...register('city', { required: 'City is required' })} />
              {errors.city && <p className="text-sm text-red-600 mt-1">{errors.city.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">State</label>
              <input className="input mt-1" {...register('state', { required: 'State is required' })} />
              {errors.state && <p className="text-sm text-red-600 mt-1">{errors.state.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Pincode</label>
              <input className="input mt-1" {...register('pincode', { required: 'Pincode is required', pattern: { value: /^[1-9][0-9]{5}$/, message: 'Enter a valid 6-digit PIN code' } })} />
              {errors.pincode && <p className="text-sm text-red-600 mt-1">{errors.pincode.message}</p>}
            </div>
          </div>

          <div className="flex justify-end">
            <button type="submit" disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomer;



